import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteParams } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chips } from "primereact/chips";
import { TaskTemplateInputSubType, TaskTemplateInputType } from "api/model/enums";
import "./TemplateEditor.css";
import {
  ITaskTemplateInputDefinition,
  TaskTemplateInputFromSubgroup,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { InputNumber } from "primereact/inputnumber";
import { createObject, handleMiniDialogCancel } from "./TemplateEditorUtils";

type InputGroupedDefinitionsSubFormsListDialogProps = {
  subForm: TaskTemplateInputFromSubgroup;
  index: number;
  onSubFormChange: (newSubForm: TaskTemplateInputFromSubgroup, index: number) => void;
};

const InputGroupedDefinitionsSubFormsListDialog: React.FC<
  InputGroupedDefinitionsSubFormsListDialogProps
> = ({ subForm, index, onSubFormChange }) => {
  const initialSubform = subForm;
  const [localSubForm, setLocalSubForm] = useState<TaskTemplateInputFromSubgroup>(subForm);
  const [dialog, setDialog] = useState(false);
  const [selectedInput, setSelectedInput] = useState<ITaskTemplateInputDefinition | null>(null);
  const [miniDialog, setMiniDialog] = useState(false);
  const [newInputId, setNewInputId] = useState("");
  const [newInputTitle, setNewInputTitle] = useState("");
  const [newInputType, setNewInputType] = useState<TaskTemplateInputType>(
    TaskTemplateInputType.INPUT_NAMED_DATASET
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempId, setTempId] = useState<string>("");
  const [unitSelectorValue, setUnitSelectorValue] = useState(selectedInput?.options?.unit || "");

  const taskTemplateInputType = Object.keys(TaskTemplateInputType).map(key => ({
    label: TaskTemplateInputType[key as keyof typeof TaskTemplateInputType],
    value: TaskTemplateInputType[key as keyof typeof TaskTemplateInputType],
  }));

  const taskTemplateInputSubType = Object.keys(TaskTemplateInputSubType).map(key => ({
    label: TaskTemplateInputSubType[key as keyof typeof TaskTemplateInputSubType],
    value: TaskTemplateInputSubType[key as keyof typeof TaskTemplateInputSubType],
  }));

  const cancelDialog = () => {
    setSelectedInput(null);
    setLocalSubForm(initialSubform);
    setDialog(false);
  };

  const saveDialog = () => {
    if (selectedInput?.id !== tempId) {
      if (localSubForm.inputs?.some(input => input.id === tempId)) {
        easasAlert({
          severity: "error",
          summary: `Input ID ${tempId} already exists. Please choose a different ID.`,
        });
        return;
      }
      setSelectedInput({
        ...selectedInput,
        id: tempId,
        title: selectedInput?.title || "",
        type: selectedInput?.type || TaskTemplateInputType.INPUT_NAMED_DATASET,
      });
    }
    if (selectedInput) {
      setLocalSubForm(prevSubForm => {
        const updatedSubForm = { ...prevSubForm };
        const inputIndex = updatedSubForm.inputs?.findIndex(input => input.id === selectedInput.id);
        if (inputIndex !== undefined && inputIndex !== -1 && updatedSubForm.inputs) {
          updatedSubForm.inputs[inputIndex] = selectedInput;
        } else {
          updatedSubForm.inputs = [...(updatedSubForm.inputs || []), selectedInput];
        }
        return updatedSubForm;
      });
    }
    onSubFormChange(localSubForm, index);
    setSelectedInput(null);
    setDialog(false);
  };

  const handleMiniDialogSubmit = () => {
    if (localSubForm.inputs?.some(input => input.id === newInputId)) {
      easasAlert({
        severity: "error",
        summary: `Input ID ${newInputId} already exists. Please choose a different ID.`,
      });
      return;
    }
    const newInput: ITaskTemplateInputDefinition = {
      id: newInputId,
      title: newInputTitle,
      type: newInputType,
      width: 6,
      options: {
        subType: TaskTemplateInputSubType.STRING,
        unit: "",
        selectOptions: [],
      },
    };
    setLocalSubForm(prevSubForm => {
      return {
        ...prevSubForm,
        inputs: [...(prevSubForm.inputs || []), newInput],
      };
    });
    setMiniDialog(false);
    setNewInputId("");
    setNewInputTitle("");
    setNewInputType(TaskTemplateInputType.INPUT_NAMED_DATASET);
  };

  const handleTypeChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = { ...selectedInput, type: value as TaskTemplateInputType };
    if (!updatedInput.options) {
      updatedInput.options = { subType: TaskTemplateInputSubType.STRING };
    }

    if (value === TaskTemplateInputType.COMMENT && updatedInput.options?.subType) {
      updatedInput.options.customConfig = { content: "" };
      delete updatedInput.options.subType;
    } else {
      updatedInput.options = {
        ...updatedInput.options,
        subType: TaskTemplateInputSubType.STRING,
        selectOptions: [],
        customConfig: {},
      };
    }
    setSelectedInput(updatedInput);
  };

  const handleSubTypeChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        subType: value as TaskTemplateInputSubType | undefined,
      },
    };
    if (value === TaskTemplateInputSubType.XY_VALUE_MATRIX) {
      updatedInput.options.customConfig = { xLabel: "", yLabel: "" };
    } else if (value === TaskTemplateInputSubType.STRING) {
      updatedInput.options.selectOptions = [];
    } else {
      delete updatedInput.options.customConfig;
      delete updatedInput.options.selectOptions;
    }
    setSelectedInput(updatedInput);
  };

  const handleUnitChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        unit: value,
      },
    };
    setUnitSelectorValue(value);
    setSelectedInput(updatedInput);
  };

  const confirmOnDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    inputIndex: number
  ) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this input?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () =>
        setLocalSubForm(prevSubForm => {
          const updatedSubForm = {
            ...prevSubForm,
            inputs: prevSubForm.inputs?.filter((_, i) => i !== inputIndex) || [],
          };
          return updatedSubForm;
        }),
    });
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteParams) => {
    const { newData } = e;
    setSelectedInput(newData);
  };

  const onRowReorder = (e: { value: ITaskTemplateInputDefinition[] }) => {
    setLocalSubForm(prevSubForm => {
      const updatedSubForm = { ...prevSubForm, inputs: e.value };
      return updatedSubForm;
    });
  };

  const dialogFooterContent = () => (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={cancelDialog}
        className="p-button-sm p-button-danger"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={saveDialog}
        className="p-button-sm p-button-success"
      />
    </div>
  );

  const renderIdInput = (
    rowData: ITaskTemplateInputDefinition,
    isTable: boolean,
    confirmOnChange?: (value: string) => void
  ) => (
    <div>
      {isTable ? (
        <div>{editingId === rowData.id ? tempId : rowData.id}</div>
      ) : (
        <div>
          <InputText
            placeholder="Id"
            value={editingId === rowData.id ? tempId : rowData.id}
            onChange={e => {
              setEditingId(rowData.id);
              setTempId(e.target.value);
            }}
            onClick={() => setSelectedInput(rowData)}
            className="p-inputtext-sm"
          />
          {selectedInput && editingId === selectedInput.id && confirmOnChange && (
            <Button
              label="Update"
              className="p-button-sm p-button-primary w-5rem"
              onClick={() => confirmOnChange(tempId)}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderTitleInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.title : rowData.title}</div>
  );

  const renderTypeInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.type : rowData.type}</div>
  );

  const renderDeleteButton = (rowData: ITaskTemplateInputDefinition, options: any) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger my-auto"
      onClick={e => confirmOnDelete(e, options.rowIndex)}
    />
  );

  const renderWidthInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.width : rowData.width}</div>
  );

  const handleXYValueMatrixUnitChange = (value: string, propertyName: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        customConfig: {
          ...selectedInput.options?.customConfig,
          [propertyName]: value,
        },
      },
    };
    setSelectedInput(updatedInput);
  };

  const confirmOnIdChange = (newId: string) => {
    confirmPopup({
      message: "Are you sure you want to change the ID?",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        if (selectedInput && !localSubForm.inputs?.find(input => input.id === newId)) {
          setLocalSubForm(prevSubForm => {
            const updatedSubForm = { ...prevSubForm };
            const inputIndex = updatedSubForm.inputs?.findIndex(
              input => input.id === selectedInput.id
            );
            if (inputIndex !== undefined && inputIndex !== -1 && updatedSubForm.inputs) {
              updatedSubForm.inputs[inputIndex] = { ...selectedInput, id: newId };
            }
            return updatedSubForm;
          });
          setSelectedInput(null);
          setEditingId(null);
          easasAlert({
            severity: "success",
            summary: `ID changed to ${newId}`,
          });
          setTempId("")
        } else {
          easasAlert({
            severity: "error",
            summary: `ID ${newId} already exists. Please choose a different ID.`,
          });
        }
      },
    });
  };

  const handleDialogClose = () => {
    setDialog(false);
    setSelectedInput(null);
    setEditingId(null);
    setTempId("");
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
      <Dialog
        modal={false}
        visible={dialog}
        blockScroll
        onHide={handleDialogClose}
        header={`"${localSubForm.header || "No Name"}" Inputs management`}
        style={{ width: "min(100%, 1300px)" }}
        footer={dialogFooterContent}
      >
        <Button
          className="p-button-success w-max mb-2"
          label="+ Input"
          onClick={() => createObject(setMiniDialog)}
        />
        <DataTable
          value={localSubForm.inputs}
          editMode="cell"
          dataKey="id"
          selection={selectedInput}
          onSelectionChange={e => {
            if (selectedInput?.id === e.value?.id) {
              setSelectedInput(null);
            } else {
              setSelectedInput(e.value);
              if (e.value?.options?.unit) setUnitSelectorValue(e.value?.options?.unit);
            }
          }}
          onRowEditComplete={onRowEditComplete}
          reorderableRows
          onRowReorder={onRowReorder}
          responsiveLayout="scroll"
          scrollHeight="300px"
          size="small"
        >
          <Column selectionMode="single" style={{ width: "2%", padding: "0.3rem" }} />
          <Column
            field="id"
            header="Id"
            style={{ padding: 0, width: "30%" }}
            body={rowData => renderIdInput(rowData, true)}
          />
          <Column
            field="title"
            header="Title"
            style={{ padding: 0, width: "30%" }}
            body={rowData => renderTitleInput(rowData)}
          />
          <Column
            field="type"
            header="Type"
            style={{ padding: 0, width: "25%" }}
            body={rowData => renderTypeInput(rowData)}
          />
          <Column
            field="width"
            header="Width"
            style={{ padding: 0, width: "10%" }}
            body={rowData => renderWidthInput(rowData)}
          />
          <Column
            style={{ padding: 0, width: "0" }}
            body={(rowData, options) => renderDeleteButton(rowData, options)}
          />
          <Column rowReorder style={{ padding: 0, justifyContent: "end", width: "5%" }} />
        </DataTable>
        <div className="subform-container mt-4">
          <div className="selected-input-details">
            <h5>Selected Input Details</h5>
            {selectedInput && (
              <div className="flex flex-row">
                <div className="p-field p-col-2">
                  <label>Id:</label>
                  {renderIdInput(selectedInput, false, confirmOnIdChange)}
                </div>
                <div className="p-field p-col-2">
                  <label>Title:</label>
                  <InputText
                    placeholder="Title"
                    value={selectedInput.title || ""}
                    onChange={e => {
                      const updatedInput = { ...selectedInput, title: e.target.value };
                      setSelectedInput(updatedInput);
                    }}
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="flex flex-column p-col-2">
                  <label className="mb-2">Type:</label>
                  <Dropdown
                    value={selectedInput.type}
                    options={taskTemplateInputType}
                    filter
                    showClear
                    onChange={e => handleTypeChange(e.value as string)}
                    optionLabel="label"
                    placeholder="Select a type"
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="p-field p-col-1">
                  <label>Width:</label>
                  <InputText
                    placeholder="Width"
                    value={selectedInput.width?.toString() || ""}
                    onChange={e => {
                      const updatedInput = { ...selectedInput, width: Number(e.target.value) };
                      setSelectedInput(updatedInput);
                    }}
                    className="p-inputtext-sm"
                  />
                </div>
                {selectedInput.type === TaskTemplateInputType.COMMENT &&
                  selectedInput.options?.customConfig && (
                    <div className="p-field p-col-2 h-100">
                      <label>Content :</label>
                      <InputText
                        placeholder="Content"
                        value={selectedInput.options.customConfig.content || ""}
                        onChange={e => {
                          const updatedInput = {
                            ...selectedInput,
                            options: {
                              ...selectedInput.options,
                              customConfig: {
                                ...selectedInput.options?.customConfig,
                                content: e.target.value,
                              },
                            },
                          };
                          setSelectedInput(updatedInput);
                        }}
                        className="p-inputtext-sm"
                      />
                    </div>
                  )}
                {selectedInput.type === TaskTemplateInputType.CONSTANT && selectedInput.options && (
                  <div>
                    <div className="flex flex-row">
                      <div className="flex p-field p-col-full m-3">
                        <label>SubType:</label>
                        <Dropdown
                          value={selectedInput.options.subType || ""}
                          options={taskTemplateInputSubType}
                          filter
                          showClear
                          onChange={e => handleSubTypeChange(e.value as string)}
                          optionLabel="label"
                          placeholder="Select a type"
                          className="p-inputtext-sm"
                        />
                      </div>
                      {selectedInput.options &&
                        selectedInput.options.subType === TaskTemplateInputSubType.NUMBER && (
                          <div className="p-field m-3">
                            <label>Decimal Places</label>
                            <InputNumber
                              value={selectedInput.options.decimalPlaces}
                              onValueChange={e => {
                                const updatedInput = {
                                  ...selectedInput,
                                  options: {
                                    ...selectedInput.options,
                                    decimalPlaces: Number(e.value),
                                  },
                                };
                                setSelectedInput(updatedInput);
                              }}
                              className="p-inputtext-sm"
                            />
                          </div>
                        )}
                    </div>

                    {selectedInput.options &&
                      selectedInput.options.subType === TaskTemplateInputSubType.STRING && (
                        <div className="p-field p-col-12">
                          <label>Selected Options:</label>
                          <Chips
                            value={selectedInput.options.selectOptions}
                            onChange={e => {
                              const updatedInput = {
                                ...selectedInput,
                                options: {
                                  ...selectedInput.options,
                                  selectOptions: e.target.value,
                                },
                              };
                              setSelectedInput(updatedInput);
                            }}
                          />
                        </div>
                      )}
                    {selectedInput.options.customConfig &&
                      selectedInput.options.subType ===
                        TaskTemplateInputSubType.XY_VALUE_MATRIX && (
                        <div className="custom-config-container">
                          <div className="flex flex-column">
                            <div className="p-field p-col-12">
                              <label>X Label:</label>
                              <InputText
                                placeholder="X Label"
                                value={selectedInput.options.customConfig.xLabel || ""}
                                onChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      customConfig: {
                                        ...selectedInput.options?.customConfig,
                                        xLabel: e.target.value,
                                      },
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                className="p-inputtext-sm"
                              />
                            </div>
                            <div className="p-field p-col-12">
                              <label>X axis unit</label>
                              <DatasetUnitSelector
                                className="p-inputtext-sm"
                                value={selectedInput.options.customConfig.xUnit || ""}
                                onChange={e => handleXYValueMatrixUnitChange(e.value, "xUnit")}
                              />
                            </div>
                          </div>

                          <div>
                            <div className="p-field p-col-12">
                              <label>Y Label:</label>
                              <InputText
                                placeholder="Y Label"
                                value={selectedInput.options.customConfig.yLabel || ""}
                                onChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      customConfig: {
                                        ...selectedInput.options?.customConfig,
                                        yLabel: e.target.value,
                                      },
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                className="p-inputtext-sm"
                              />
                            </div>
                            <div className="p-field p-col-12">
                              <label>Y axis unit</label>
                              <DatasetUnitSelector
                                className="p-inputtext-sm"
                                value={selectedInput.options.customConfig.yUnit || ""}
                                onChange={e => handleXYValueMatrixUnitChange(e.value, "yUnit")}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                    {selectedInput.options &&
                      selectedInput.options.subType === TaskTemplateInputSubType.NUMBER && (
                        <div className="p-field p-col-6">
                          <label>Unit:</label>
                          <DatasetUnitSelector
                            className="p-inputtext-sm"
                            value={unitSelectorValue}
                            onChange={e => {
                              handleUnitChange(e.value);
                            }}
                          />
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>
      <Dialog
        header="Create New Input"
        blockScroll
        visible={miniDialog}
        onHide={() => setMiniDialog(false)}
        className="w-2"
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => handleMiniDialogCancel(setMiniDialog, setNewInputId, setNewInputId)}
              className="p-button-sm p-button-danger"
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleMiniDialogSubmit}
              className="p-button-sm p-button-success"
            />
          </div>
        }
      >
        <div className="p-field">
          <label htmlFor="newInputId">ID</label>
          <InputText
            id="newInputId"
            value={newInputId || ""}
            onChange={e => setNewInputId(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="p-field">
          <label htmlFor="newInputTitle">Title</label>
          <InputText
            id="newInputTitle"
            value={newInputTitle || ""}
            onChange={e => setNewInputTitle(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="p-field">
          <label htmlFor="newInputType">Type</label>
          <Dropdown
            id="newInputType"
            value={newInputType || ""}
            options={taskTemplateInputType}
            filter
            showClear
            onChange={e => setNewInputType(e.value as TaskTemplateInputType)}
            optionLabel="label"
            placeholder="Select a type"
            className="p-inputtext-sm"
          />
        </div>
      </Dialog>
    </>
  );
};

export default InputGroupedDefinitionsSubFormsListDialog;
